/**
 * Shared multi-page utilities used by both campaigns and tools.
 */
import type { CSSProperties } from 'react';
import type { MultiPageDef, PageSettings } from '../../types';

export type { PageSettings };

/** Effective defaults for pageSettings — progress on top, buttons on bottom. */
export const DEFAULT_PAGE_SETTINGS: Required<PageSettings> = {
  transition: 'fade',
  transitionSpeed: 'normal',
  navPosition: 'bottom',
  progressPosition: 'top',
  progressStyle: 'pills',
};

/** Merge stored settings over the defaults. */
export function resolvePageSettings(s?: PageSettings | null): Required<PageSettings> {
  return { ...DEFAULT_PAGE_SETTINGS, ...(s || {}) };
}

const SPEED_MS: Record<Required<PageSettings>['transitionSpeed'], number> = {
  fast: 200,
  normal: 400,
  slow: 700,
};

/**
 * Inline animation style for the page container. Re-keyed on page switch, the
 * container remounts and this entry animation plays. Transforms are safe on a
 * wrapper of absolutely-positioned blocks: the wrapper sits at the canvas
 * origin, so while it is a containing block (transform ≠ none) the children's
 * coordinates are unchanged.
 */
export function pageTransitionStyle(s?: PageSettings | null): CSSProperties {
  const { transition, transitionSpeed } = resolvePageSettings(s);
  if (transition === 'none') return {};
  const name = {
    fade: 'mpPageFade',
    slide: 'mpPageSlide',
    zoom: 'mpPageZoom',
  }[transition];
  return { animation: `${name} ${SPEED_MS[transitionSpeed]}ms ease both` };
}

/**
 * Keyframes for the page transitions. Injected once (as a <style> tag) by any
 * surface that renders multi-page content: builder canvas + both public
 * renderers. Slide enters from the left, matching RTL "next" direction.
 */
export const MULTIPAGE_KEYFRAMES_CSS = `
@keyframes mpPageFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes mpPageSlide { from { opacity: 0; transform: translateX(-32px); } to { opacity: 1; transform: none; } }
@keyframes mpPageZoom { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: none; } }
`;

/** Generate a short unique ID for pages */
export function genPageId(): string {
  return 'pg_' + Math.random().toString(36).slice(2, 9);
}

/** Create a default first page */
export function createDefaultPage(name = 'עמוד 1'): MultiPageDef {
  return { id: genPageId(), name, order: 0 };
}

/** Sort pages by order */
export function sortedPages(pages: MultiPageDef[]): MultiPageDef[] {
  return [...pages].sort((a, b) => a.order - b.order);
}

/** Get the first page id, or undefined if no pages */
export function firstPageId(pages?: MultiPageDef[]): string | undefined {
  if (!pages || pages.length === 0) return undefined;
  return sortedPages(pages)[0].id;
}

/**
 * Resolve which page a block belongs to.
 * If blockPageId is set and exists in pages, use it.
 * Otherwise default to first page.
 */
export function resolveBlockPageId(
  blockPageId: string | undefined,
  pages: MultiPageDef[]
): string {
  if (blockPageId && pages.some(p => p.id === blockPageId)) return blockPageId;
  return sortedPages(pages)[0].id;
}

/** Check if multi-page mode is active */
export function isMultiPage(pages?: MultiPageDef[]): boolean {
  return Array.isArray(pages) && pages.length > 1;
}

/** Reorder pages after move */
export function reorderPages(pages: MultiPageDef[], fromIdx: number, toIdx: number): MultiPageDef[] {
  const sorted = sortedPages(pages);
  const [moved] = sorted.splice(fromIdx, 1);
  sorted.splice(toIdx, 0, moved);
  return sorted.map((p, i) => ({ ...p, order: i }));
}

/**
 * Check if it's safe to delete a page (must have at least 1 page remaining).
 */
export function canDeletePage(pages: MultiPageDef[]): boolean {
  return pages.length > 1;
}
